import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import ContestedIcon from '@assets/images/observation-status-contested-icon.svg?react'
import DoneIcon from '@assets/images/observation-status-done-icon.svg?react'
import ToDoIcon from '@assets/images/observation-status-todo-icon.svg?react'

import { ObservationStatus, ProjectRole, UserRole } from '@types'

import { Option, Select } from '@components/Select'

import { ItemContainer, ItemLabel, Status, StatusContainer } from './SelectObservationStatus.styled'

interface Props {
  required?: boolean
  status?: ObservationStatus
  onSelectObservationStatus: (status?: ObservationStatus) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  dropdownAutoWidth?: boolean
  projectACL?: ProjectRole[]
  userRole?: UserRole
}

const getAvailableStatuses = (roles?: ProjectRole[], userRole?: UserRole): ObservationStatus[] => {
  const basicStatuses = [ObservationStatus.StatusToDo, ObservationStatus.StatusContested]

  if (
    !roles ||
    roles.length === 0 ||
    userRole === UserRole.RoleAdmin ||
    roles?.includes(ProjectRole.RoleProjectAdmin)
  ) {
    return [
      ...basicStatuses,
      ObservationStatus.StatusValidatedByCustomer,
      ObservationStatus.StatusValidatedByAdmin,
      ObservationStatus.StatusValidatedByReader,
    ]
  }

  if (roles?.includes(ProjectRole.RoleProjectCustomer)) {
    return [...basicStatuses, ObservationStatus.StatusValidatedByCustomer]
  }

  if (roles?.includes(ProjectRole.RoleProjectReader)) {
    return [...basicStatuses, ObservationStatus.StatusValidatedByReader]
  }

  return basicStatuses
}

export const SelectObservationStatus: React.FC<Props> = (props: Props): JSX.Element => {
  const {
    required,
    status,
    dropdownAutoWidth = false,
    label,
    placeholder,
    disabled = false,
    onSelectObservationStatus,
    projectACL,
    userRole,
  } = props

  const { t } = useTranslation()

  const availableStatuses = useMemo(() => getAvailableStatuses(projectACL, userRole), [projectACL, userRole])

  const allOptions: Option[] = useMemo(
    () => [
      {
        id: ObservationStatus.StatusToDo,
        label: t('enum_ObservationStatus_to_do_label'),
        data: ObservationStatus.StatusToDo,
        Component: () => <ObservationStatusRow status={ObservationStatus.StatusToDo} selectable />,
      },
      {
        id: ObservationStatus.StatusContested,
        label: t('enum_ObservationStatus_contested'),
        data: ObservationStatus.StatusContested,
        Component: () => <ObservationStatusRow status={ObservationStatus.StatusContested} selectable />,
      },
      {
        id: ObservationStatus.StatusValidatedByReader,
        label: t('enum_ObservationStatus_validated_by_reader'),
        data: ObservationStatus.StatusValidatedByReader,
        Component: () => <ObservationStatusRow status={ObservationStatus.StatusValidatedByReader} selectable />,
      },
      {
        id: ObservationStatus.StatusValidatedByAdmin,
        label: t('enum_ObservationStatus_validated_by_admin'),
        data: ObservationStatus.StatusValidatedByAdmin,
        Component: () => <ObservationStatusRow status={ObservationStatus.StatusValidatedByAdmin} selectable />,
      },
      {
        id: ObservationStatus.StatusValidatedByCustomer,
        label: t('enum_ObservationStatus_validated_by_customer'),
        data: ObservationStatus.StatusValidatedByCustomer,
        Component: () => <ObservationStatusRow status={ObservationStatus.StatusValidatedByCustomer} selectable />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const selectableOptions = useMemo(
    () => allOptions.filter((option) => availableStatuses.includes(option.data as ObservationStatus)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableStatuses],
  )

  const selectedOption = useMemo(
    () => (status ? allOptions.find((option) => option.id === status) : undefined),
    [status, allOptions],
  )

  const isDisabled = disabled

  return (
    <Select
      required={required}
      label={label}
      dropdownAutoWidth={dropdownAutoWidth}
      placeholder={placeholder}
      selected={selectedOption}
      options={selectableOptions}
      disabled={isDisabled}
      onClick={(option) => {
        if (option && option.data !== selectedOption?.data) {
          onSelectObservationStatus(option.data as ObservationStatus)
        } else {
          onSelectObservationStatus()
        }
      }}
    />
  )
}

interface ObservationStatusRowProps {
  status: ObservationStatus
  labelColor?: 'dark' | 'light'
  selectable?: boolean
}

export const ObservationStatusRow = (props: ObservationStatusRowProps): JSX.Element => {
  const { status, labelColor = 'dark', selectable = false } = props

  const { t } = useTranslation()

  return (
    <ItemContainer>
      <StatusContainer>
        <Status status={status}>
          {status === ObservationStatus.StatusToDo && <ToDoIcon width={18} height={18} />}
          {/* {status === ObservationStatus.StatusPlannedRecovery && <RecoveryIcon />} */}
          {status === ObservationStatus.StatusContested && <ContestedIcon />}
          {status === ObservationStatus.StatusValidatedByReader && <DoneIcon />}
          {status === ObservationStatus.StatusValidatedByCustomer && <DoneIcon />}
          {status === ObservationStatus.StatusValidatedByAdmin && <DoneIcon />}
        </Status>
      </StatusContainer>

      <ItemLabel labelColor={labelColor} selectable={selectable}>
        {status === ObservationStatus.StatusToDo && t('enum_ObservationStatus_to_do_label')}
        {/* {status === ObservationStatus.StatusPlannedRecovery && t('enum_ObservationStatus_planned_recovery')} */}
        {status === ObservationStatus.StatusContested && t('enum_ObservationStatus_contested')}
        {status === ObservationStatus.StatusValidatedByReader && t('enum_ObservationStatus_validated_by_reader')}
        {status === ObservationStatus.StatusValidatedByCustomer && t('enum_ObservationStatus_validated_by_customer')}
        {status === ObservationStatus.StatusValidatedByAdmin && t('enum_ObservationStatus_validated_by_admin')}
      </ItemLabel>
    </ItemContainer>
  )
}
